// CoreUI Pro source reference: /Users/guochunzhong/git/oss/coreui-pro/js/src/time-picker.js
import { createPopper } from "@popperjs/core"
import BaseComponent from "./base_component"
import Calendar from "./calendar"
import { dateDisabled, dateRangeContainsDisabled, initialCalendarDate, localDate, parseDate, parseTime } from "./dom"

import {
  delayedInput,
  pickerLabel,
  button,
  twoDigits,
  timeFormat,
  parsePickerDate,
  attachMenu,
  showMenu,
  pickerTab,
  hideMenu,
} from "./picker-utils"

export default class TimePicker extends BaseComponent {
  static componentName = "time-picker"
  static defaults = {
    cancelButton: "Cancel",
    cleaner: true,
    confirmButton: "OK",
    disabled: false,
    footer: true,
    indicator: true,
    inputReadOnly: false,
    locale: "default",
    minutes: true,
    placeholder: "Select time",
    required: true,
    seconds: true,
    type: "dropdown",
    variant: "roll",
  }

  constructor(element, config = {}) {
    super(element, config)
    if (["inline", "dropdown"].includes(this.config.container)) {
      this.config.type = this.config.container
      this.config.container = false
    }
    if (this.config.minutes === false) this.config.seconds = false
    if (!this.config.time && this.config.value) this.config.time = this.config.value
    this.date = parseTime(this.config.time)
    this.initialDate = this.date ? new Date(this.date) : null
    this.render()
    attachMenu(this)
    this.bindEvents()
    if (this.config.readonly) {
      element.setAttribute("aria-readonly", "true")
      if (this.input) this.input.readOnly = true
      element.querySelector(".time-picker-indicator")?.setAttribute("tabindex", "-1")
      for (const type of ["click", "keydown"])
        this.listen(element, type, (event) => event.stopImmediatePropagation(), { capture: true })
    }
  }

  render() {
    this.element.replaceChildren()
    this.element.className = this.originalClassName
    this.element.classList.add("time-picker")
    if (this.config.size) this.element.classList.add(`time-picker-${this.config.size}`)
    if (this.config.disabled) this.element.classList.add("disabled")
    this.element.setAttribute("aria-expanded", "false")

    if (this.config.type === "dropdown") {
      this.element.append(this.createInputGroup())
      this.menu = document.createElement("div")
      this.menu.className = "time-picker-dropdown"
      this.menu.append(this.createBody())
      if (this.config.footer) this.menu.append(this.createFooter())
      this.element.append(this.menu)
    } else {
      this.element.append(this.createBody())
    }
    this.updateSelection()
  }

  createInputGroup() {
    const group = document.createElement("div")
    group.className = "time-picker-input-group"
    this.input = document.createElement("input")
    this.input.className = "time-picker-input"
    this.input.type = "text"
    this.input.autocomplete = "off"
    this.input.placeholder = this.config.placeholder
    this.input.disabled = Boolean(this.config.disabled)
    this.input.readOnly = Boolean(this.config.inputReadOnly)
    this.input.required = Boolean(this.config.required)
    this.input.value = this.format(this.date)
    if (pickerLabel(this.element)) this.input.setAttribute("aria-label", pickerLabel(this.element))
    if (this.config.name) this.input.name = this.config.name
    group.append(this.input)

    if (this.config.indicator) {
      this.indicator = document.createElement("div")
      this.indicator.className = "time-picker-indicator"
      if (!this.config.disabled) this.indicator.tabIndex = 0
      group.append(this.indicator)
    }
    if (this.config.cleaner) {
      const cleaner = document.createElement("div")
      cleaner.className = "time-picker-cleaner"
      cleaner.setAttribute("aria-label", "Clear time")
      this.listen(cleaner, "click", (event) => {
        event.stopPropagation()
        this.clear()
      })
      group.append(cleaner)
    }
    this.toggler = group
    return group
  }

  createBody() {
    this.body = document.createElement("div")
    this.body.className = `time-picker-body ${this.config.variant === "roll" ? "time-picker-roll" : ""}`.trim()
    this.body.setAttribute("role", "group")
    if (this.config.variant === "select") this.createSelects()
    else this.createRolls()
    return this.body
  }

  createRolls() {
    const hour12 = this.hour12()
    const hours = hour12
      ? Array.from({ length: 12 }, (_, index) => index + 1)
      : Array.from({ length: 24 }, (_, index) => index)
    this.body.append(this.createRoll(hours, "hours", "Select hours", (value) => (hour12 ? value : twoDigits(value))))
    if (this.config.minutes)
      this.body.append(
        this.createRoll(
          Array.from({ length: 60 }, (_, index) => index),
          "minutes",
          "Select minutes",
          twoDigits,
        ),
      )
    if (this.config.seconds)
      this.body.append(
        this.createRoll(
          Array.from({ length: 60 }, (_, index) => index),
          "seconds",
          "Select seconds",
          twoDigits,
        ),
      )
    if (hour12)
      this.body.append(this.createRoll(["am", "pm"], "meridiem", "Select AM/PM", (value) => value.toUpperCase()))
  }

  availableValues(values, part) {
    const allowed = this.config[part]
    if (Array.isArray(allowed) && allowed.length) return values.filter((value) => allowed.includes(value))
    if (typeof allowed === "function") return values.filter(allowed)
    return values
  }

  createRoll(values, part, ariaLabel, formatter) {
    values = this.availableValues(values, part)
    const column = document.createElement("div")
    column.className = "time-picker-roll-col"
    column.setAttribute("role", "listbox")
    column.dataset.timePart = part
    column.setAttribute(
      "aria-label",
      this.config[`ariaSelect${part[0].toUpperCase()}${part.slice(1)}Label`] || ariaLabel,
    )
    values.forEach((value, index) => {
      const cell = document.createElement("div")
      cell.className = "time-picker-roll-cell"
      cell.setAttribute("role", "option")
      cell.setAttribute("aria-selected", "false")
      cell.tabIndex = index === 0 ? 0 : -1
      cell.dataset[`coreui${part[0].toUpperCase()}${part.slice(1)}`] = value
      cell.textContent = formatter(value)
      column.append(cell)
    })
    return column
  }

  createSelects() {
    const parts = [
      [
        "hours",
        this.hour12()
          ? Array.from({ length: 12 }, (_, index) => index + 1)
          : Array.from({ length: 24 }, (_, index) => index),
      ],
      ["minutes", this.config.minutes ? Array.from({ length: 60 }, (_, index) => index) : []],
      ["seconds", this.config.seconds ? Array.from({ length: 60 }, (_, index) => index) : []],
      ["meridiem", this.hour12() ? ["am", "pm"] : []],
    ]
    parts.forEach(([part, values], index) => {
      values = this.availableValues(values, part)
      if (!values.length) return
      if (index > 0 && part !== "meridiem") this.body.append(document.createTextNode(":"))
      const select = document.createElement("select")
      select.className = `time-picker-inline-select ${part}`
      select.disabled = Boolean(this.config.disabled)
      select.setAttribute(
        "aria-label",
        this.config[`ariaSelect${part[0].toUpperCase()}${part.slice(1)}Label`] || `Select ${part}`,
      )
      values.forEach((value) => {
        const option = document.createElement("option")
        option.value = value
        option.textContent = part === "meridiem" ? value.toUpperCase() : twoDigits(value)
        select.append(option)
      })
      this.body.append(select)
    })
  }

  createFooter() {
    const footer = document.createElement("div")
    footer.className = "time-picker-footer"
    if (this.config.cancelButton)
      footer.append(button(this, this.config.cancelButton, "btn btn-sm btn-ghost-primary", () => this.cancel()))
    if (this.config.confirmButton)
      footer.append(button(this, this.config.confirmButton, "btn btn-sm btn-primary", () => this.hide()))
    return footer
  }

  bindEvents() {
    this.listen(this.body, "click", (event) => {
      const cell = event.target.closest(".time-picker-roll-cell")
      if (cell) return this.change(cell.parentElement.dataset.timePart, cell.dataset[Object.keys(cell.dataset)[0]])
    })
    this.listen(this.element, "click", (event) => {
      if (event.target.closest(".time-picker-input-group") && !event.target.closest(".time-picker-cleaner"))
        this.toggle()
    })
    this.listen(this.body, "change", (event) => {
      if (event.target.matches(".time-picker-inline-select")) {
        const part = [...event.target.classList].find((name) =>
          ["hours", "minutes", "seconds", "meridiem"].includes(name),
        )
        this.change(part, event.target.value)
      }
    })
    if (this.input)
      delayedInput(this, this.input, () => {
        if (this.updatingInput || this.config.disabled || this.config.inputReadOnly) return
        const date = parseTime(this.input.value)
        if (date || !this.input.value.trim()) this.setTime(date)
      })
    const keydown = (event) => {
      pickerTab(this, event)
      if (event.key === "Escape") this.hide()
      if ((event.key === "Enter" || event.key === " ") && event.target === this.indicator) {
        event.preventDefault()
        this.toggle()
      }
      const cell = event.target.closest(".time-picker-roll-cell")
      if (cell && ["ArrowDown", "ArrowUp", "Home", "End", "Enter", " "].includes(event.key)) {
        event.preventDefault()
        const cells = [...cell.parentElement.children]
        const index = cells.indexOf(cell)
        const next =
          event.key === "Home"
            ? 0
            : event.key === "End"
              ? cells.length - 1
              : event.key === "ArrowDown"
                ? (index + 1) % cells.length
                : event.key === "ArrowUp"
                  ? (index - 1 + cells.length) % cells.length
                  : index
        const target = cells[next]
        this.change(cell.parentElement.dataset.timePart, target.dataset[Object.keys(target.dataset)[0]])
        target.focus()
      }
    }
    this.listen(this.element, "keydown", keydown)
    if (this.detachedMenu) this.listen(this.menu, "keydown", keydown)
    this.listen(document, "click", (event) => {
      if (!this.element.contains(event.target) && !this.menu?.contains(event.target)) this.hide()
    })
  }

  hour12() {
    return new Intl.DateTimeFormat(this.config.locale, { hour: "numeric" }).resolvedOptions().hour12
  }

  selectedPart(part) {
    if (!this.date) return null
    if (part === "hours") {
      const hours = this.date.getHours()
      return this.hour12() ? hours % 12 || 12 : hours
    }
    if (part === "minutes") return this.date.getMinutes()
    if (part === "seconds") return this.date.getSeconds()
    return this.date.getHours() >= 12 ? "pm" : "am"
  }

  change(part, value) {
    if (this.config.disabled) return
    const date = this.date ? new Date(this.date) : new Date(1970, 0, 1)
    if (part === "meridiem") {
      const hours = date.getHours()
      if (value === "pm" && hours < 12) date.setHours(hours + 12)
      if (value === "am" && hours >= 12) date.setHours(hours - 12)
    } else if (part === "hours") {
      let hours = Number(value)
      if (this.hour12()) {
        const pm = date.getHours() >= 12
        hours = (hours % 12) + (pm ? 12 : 0)
      }
      date.setHours(hours)
    } else if (part === "minutes") date.setMinutes(Number(value))
    else if (part === "seconds") date.setSeconds(Number(value))
    this.setTime(date)
  }

  setTime(date) {
    this.date = date ? new Date(date) : null
    if (this.input) this.input.value = this.format(this.date)
    this.updateSelection()
    if (this.input) {
      this.updatingInput = true
      this.input.dispatchEvent(new Event("change", { bubbles: true }))
      this.updatingInput = false
    }
    this.emit("timeChange", {
      timeString: this.date?.toTimeString() || null,
      localeTimeString: this.format(this.date) || null,
      date: this.date,
    })
    this.config.onChange?.(this.date)
  }

  updateSelection() {
    this.body.querySelectorAll(".time-picker-roll-col").forEach((column) => {
      const part = column.dataset.timePart
      const value = String(this.selectedPart(part))
      column.querySelectorAll(".time-picker-roll-cell").forEach((cell) => {
        const selected = String(cell.dataset[Object.keys(cell.dataset)[0]]) === value
        cell.classList.toggle("selected", selected)
        cell.tabIndex = selected || (!this.date && cell === column.firstElementChild) ? 0 : -1
        cell.setAttribute("aria-selected", selected)
      })
    })
    this.body.querySelectorAll(".time-picker-inline-select").forEach((select) => {
      const part = [...select.classList].find((name) => ["hours", "minutes", "seconds", "meridiem"].includes(name))
      const value = this.selectedPart(part)
      if (value !== null) select.value = value
      else select.selectedIndex = -1
    })
  }

  format(date) {
    if (!date) return ""
    return date.toLocaleTimeString(this.config.locale, timeFormat(this.config))
  }

  show() {
    if (this.config.disabled || this.config.type !== "dropdown" || this.element.classList.contains("show")) return
    this.initialDate = this.date ? new Date(this.date) : null
    showMenu(this)
    this.element.classList.add("show")
    this.element.setAttribute("aria-expanded", "true")
    this.emit("shown")
  }

  hide() {
    if (!this.element.classList.contains("show")) return
    hideMenu(this)
    this.element.classList.remove("show")
    this.element.setAttribute("aria-expanded", "false")
    this.emit("hidden")
  }

  toggle() {
    this.element.classList.contains("show") ? this.hide() : this.show()
  }
  clear() {
    this.setTime(null)
  }

  cancel() {
    this.setTime(this.initialDate)
    this.hide()
  }

  dispose() {
    if (this.signal.aborted) return
    hideMenu(this)
    this.restoreAttribute("aria-expanded")
    this.element.innerHTML = this.originalHTML
    this.element.className = this.originalClassName
    super.dispose()
  }
  snapshotState() {
    const time = this.date
      ? [this.date.getHours(), this.date.getMinutes(), this.date.getSeconds()].map(twoDigits).join(":")
      : null
    return { config: { time, value: time }, text: this.input?.value }
  }
  restoreState(state) {
    if (this.input && state.text !== undefined) this.input.value = state.text
  }
}
